import classnames from 'classnames'
import { isEmpty, noop } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import Autosuggest, {
  AutosuggestPropsSingleSection,
  InputProps,
  RenderSuggestionParams,
} from 'react-autosuggest'
import ReactDOM from 'react-dom'
import sleep from 'sleep-promise'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import Spinner from '../Spinner'

const Wrapper = styled.div`
  position: relative;
`

const SpinnerWrapper = styled.div`
  position: absolute;
  right: 0.6em;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
`

type TSuggestions =
  | string[]
  | {
      [key: string]: any
    }[]
export interface TTypeAhead
  extends Omit<
    AutosuggestPropsSingleSection<string>,
    | 'suggestions'
    | 'value'
    | 'getSuggestionValue'
    | 'inputProps'
    | 'onSuggestionsFetchRequested'
    | 'renderSuggestion'
  > {
  value?: string | null
  requestOptions?: (searchBy?: string, value?: string) => Promise<any[]>
  renderOption?: (
    suggestion: any,
    matcher: RenderSuggestionParams,
  ) => React.ReactNode
  onSetValues?: (values: any) => void
  searchBy?: string
  isInvalid?: boolean
  minLength?: number
  className?: string
  useBodyOption?: boolean
  delay?: number
  noOptions?: boolean
  setResult?: () => void
  onChange?: (val: string) => void
  suggestions?: TSuggestions
  ref?: any
  placeholder?: string
}
type inputBounding = {
  top?: number
  left?: number
}

const defaultRenderOption = (suggestion: { text: string }): React.ReactNode => {
  return <span>{suggestion.text}</span>
}
const TypeAhead = (props: TTypeAhead): React.ReactElement => {
  const {
    requestOptions = noop,
    renderOption = defaultRenderOption,
    onSetValues = noop,
    onSuggestionSelected = noop,
    searchBy,
    isInvalid,
    minLength = 4,
    className,
    suggestions = [],
    useBodyOption = false,
    delay = 250,
    noOptions,
    setResult = noop,
  } = props

  const [value, setValue] = useState<string>(props.value || '')
  const [error, setError] = useState(undefined)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setValue(props.value || '')
  }, [props.value])

  const [options, setOptions] = useState<TSuggestions>([])

  useEffect(() => {
    if (!isEmpty(suggestions)) {
      setOptions(suggestions)
    }
  }, [suggestions])

  const lock = useRef<string>()
  const inputRef = useRef<HTMLInputElement>()

  // /const requestOptionsDebounced = debounce(loadOptions, debounceTime)

  const loadOptions = async (value): Promise<void> => {
    if (value.value.length < minLength) {
      setOptions([])
      return
    }

    const id = uuid()
    lock.current = id
    // setLatestLoadId(latestId)
    // if (latestId !== latestLoadId) {
    //   return
    // }

    let error, result

    setIsLoading(true)

    await sleep(delay)

    if (id !== lock.current) {
      return
    }

    try {
      result = await requestOptions(searchBy, value.value)
    } catch (err) {
      error = err
    }

    if (id !== lock.current) {
      return
    }

    if (error) {
      setOptions([])
      setError(error)
    } else {
      if (noOptions) {
        setResult(result)
      } else {
        setOptions(result)
      }
    }
    setIsLoading(false)
  }

  const onChange = (e, { newValue, method }): void => {
    if (method === 'type') {
      if (props.onChange) {
        props.onChange(newValue)
      }
    } else {
      onSetValues(newValue)
    }
  }

  const inputProps = {
    ...props,
    value: value,
    onChange: onChange,
    className: classnames(
      'form-control',
      {
        'is-invalid': isInvalid,
      },
      className,
    ),
    ref: inputRef,
  }

  // const onSuggestionHighlighted = value => {
  //   console.log("highlighting", value)
  //   const city = get(value, "suggestion.city")
  //   if (city) {
  //     onChangeCity(city)
  //   } else {
  //   }
  // }

  const renderSuggestionsBodyContainer = (suggestProps): React.ReactElement => {
    const { containerProps, children } = suggestProps
    const inputBounding: inputBounding =
      inputRef.current && !isEmpty(inputRef.current)
        ? inputRef.current.getBoundingClientRect()
        : {}
    const { left = 0, top = 0 } = inputBounding || {}
    const customStyle = {
      position: 'absolute',
      left: left + window.scrollX,
      top: top + 42 + window.scrollY,
    }
    return ReactDOM.createPortal(
      <div
        {...containerProps}
        style={customStyle}
        ref={(c) => {
          containerProps.ref(c)
        }}
      >
        {children}
      </div>,
      document.body,
    )
  }
  return (
    <Wrapper>
      <Autosuggest
        onSuggestionSelected={onSuggestionSelected || onSetValues}
        inputProps={inputProps as InputProps<string>}
        suggestions={options as string[]}
        onSuggestionsFetchRequested={loadOptions}
        onSuggestionsClearRequested={noop}
        //onSuggestionHighlighted={onSuggestionHighlighted}
        getSuggestionValue={(suggestion) => suggestion}
        renderSuggestion={(suggestion, matcher) =>
          renderOption(suggestion, matcher)
        }
        highlightFirstSuggestion
        // renderInputComponent={({ ref, ...rest }) => (
        //   <Input isLoading={isLoading} {...rest} innerRef={ref} />
        // )}
        theme={{
          container: 'autosuggest',
          input: 'form-control',
          suggestionsContainer: 'dropdown',
          suggestionsList: `dropdown-menu ${
            options.length ? 'show' : ''
          } dropdown-menu-full-width dropdown-menu-more-shadow`,
          suggestionFocused: 'active',
          suggestion: 'dropdown-item',
          suggestionHighlighted: 'dropdown-item hovered',
        }}
        {...(useBodyOption && {
          renderSuggestionsContainer: renderSuggestionsBodyContainer,
        })}
      />

      {isLoading && (
        <SpinnerWrapper>
          <Spinner size={20} />
        </SpinnerWrapper>
      )}
    </Wrapper>
  )
}

export default TypeAhead
